import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import api, { STAGES, channelColor, API } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, X, Sparkles, Trash2, Link2, Filter, KanbanSquare, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";

const PRIORITY = {
  high: { color: "#D93D3D", label: "High" },
  medium: { color: "#D9A441", label: "Medium" },
  low: { color: "#66666E", label: "Low" },
};

export default function Board() {
  const [cards, setCards] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterChannel, setFilterChannel] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dropStage, setDropStage] = useState(null);
  const [editing, setEditing] = useState(null); // card object or {new:true}
  const [view, setView] = useState("board"); // "board" | "calendar"
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  const load = async () => {
    const [c, ch] = await Promise.all([api.get("/cards"), api.get("/channels")]);
    setCards(c.data);
    setChannels(ch.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return cards.filter((c) => {
      if (filterChannel !== "all" && c.channel_id !== filterChannel) return false;
      if (overdueOnly) {
        if (!c.due_date || c.stage === "Published") return false;
        if (new Date(c.due_date) > new Date()) return false;
      }
      return true;
    });
  }, [cards, filterChannel, overdueOnly]);

  const byStage = (s) => filtered.filter((c) => c.stage === s);

  const onDrop = async (stage) => {
    setDropStage(null);
    const id = dragId;
    setDragId(null);
    if (!id) return;
    const card = cards.find((c) => c.id === id);
    if (!card || card.stage === stage) return;
    setCards((prev) => prev.map((c) => (c.id === id ? { ...c, stage } : c)));
    try {
      const { data } = await api.put(`/cards/${id}/move`, { stage });
      setCards((prev) => prev.map((c) => (c.id === id ? data : c)));
    } catch {
      toast.error("Failed to move card");
      load();
    }
  };

  const chName = (id) => channels.find((c) => c.id === id)?.name || "—";

  return (
    <div className="fade-up">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="font-mono text-xs text-primary tracking-[0.25em] uppercase mb-2">Production pipeline</p>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight">Board</h1>
        </div>
        <button
          onClick={() => setEditing({ new: true })}
          data-testid="new-card-button"
          className="flex items-center gap-2 bg-primary text-primary-foreground font-bold rounded-md px-4 py-2.5 text-sm hover:bg-[#C29035] transition-colors duration-200"
        >
          <Plus size={18} /> New card
        </button>
      </header>

      {/* Filters + view toggle */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center gap-2 text-muted-foreground"><Filter size={15} /><span className="text-xs font-mono uppercase tracking-wider">Filter</span></div>
        <select
          value={filterChannel}
          onChange={(e) => setFilterChannel(e.target.value)}
          data-testid="filter-channel"
          className="bg-[#1A1A1C] border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-primary transition-colors duration-200"
        >
          <option value="all">All channels</option>
          {channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {view === "board" && (
          <button
            onClick={() => setOverdueOnly(!overdueOnly)}
            data-testid="filter-overdue"
            className={`text-sm font-medium rounded-md px-3 py-2 border transition-colors duration-200 ${overdueOnly ? "bg-destructive/15 text-destructive border-destructive/50" : "border-border text-muted-foreground hover:bg-secondary"}`}
          >
            Overdue only
          </button>
        )}

        {/* View toggle */}
        <div className="ml-auto inline-flex rounded-md border border-border overflow-hidden" data-testid="view-toggle">
          <button
            onClick={() => setView("board")}
            data-testid="view-board"
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors duration-200 ${view === "board" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}
          >
            <KanbanSquare size={15} /> Board
          </button>
          <button
            onClick={() => setView("calendar")}
            data-testid="view-calendar"
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors duration-200 ${view === "calendar" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}
          >
            <CalendarDays size={15} /> Calendar
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : view === "calendar" ? (
        <CalendarView
          cards={cards.filter((c) => c.due_date && (filterChannel === "all" || c.channel_id === filterChannel))}
          channels={channels}
          month={calMonth}
          setMonth={setCalMonth}
          onCardClick={(card) => setEditing(card)}
        />
      ) : (
        <div className="flex gap-2.5 overflow-x-auto pb-4">
          {STAGES.map((stage) => {
            const list = byStage(stage);
            return (
              <div
                key={stage}
                data-testid={`column-${stage.toLowerCase()}`}
                onDragOver={(e) => { e.preventDefault(); setDropStage(stage); }}
                onDragLeave={() => setDropStage((s) => (s === stage ? null : s))}
                onDrop={() => onDrop(stage)}
                className={`flex-1 min-w-[140px] bg-[#121214] border rounded-lg p-2.5 transition-colors duration-200 ${dropStage === stage ? "drop-active" : "border-border"}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display font-bold text-sm tracking-tight">{stage}</h3>
                  <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{list.length}</span>
                </div>
                <div className="space-y-2.5 min-h-[60px]">
                  {list.map((card) => (
                    <KanbanCard
                      key={card.id}
                      card={card}
                      channelName={chName(card.channel_id)}
                      channelColorHex={channelColor(card.channel_id, channels)}
                      onDragStart={() => setDragId(card.id)}
                      onDragEnd={() => { setDragId(null); setDropStage(null); }}
                      dragging={dragId === card.id}
                      onClick={() => setEditing(card)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <CardModal
          card={editing.new ? null : editing}
          channels={channels}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function KanbanCard({ card, channelName, channelColorHex, onDragStart, onDragEnd, dragging, onClick }) {
  const pri = PRIORITY[card.priority] || PRIORITY.medium;
  const due = card.due_date ? new Date(card.due_date) : null;
  const overdue = due && card.stage !== "Published" && due < new Date();
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      data-testid={`card-${card.id}`}
      className={`bg-[#1A1A1C] border border-border rounded-md p-2.5 cursor-pointer hover:border-primary/50 hover:-translate-y-0.5 transition-all duration-200 ${dragging ? "drag-ghost" : ""}`}
      style={{ borderLeft: `3px solid ${pri.color}` }}
    >
      <div className="flex items-start justify-between gap-1.5 mb-1.5">
        <p className="text-[13px] font-semibold leading-snug">{card.title}</p>
        {card.stalled && (
          <span className="shrink-0 font-mono text-[9px] font-bold uppercase px-1 py-0.5 rounded bg-destructive/20 text-destructive border border-destructive/50 animate-pulse">Stalled</span>
        )}
      </div>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
          <span className="h-2 w-2 rounded-full" style={{ background: channelColorHex }} />
          {channelName}
        </span>
      </div>
      {card.notes && <p className="text-[11px] text-muted-foreground line-clamp-2 mb-1.5">{card.notes}</p>}
      <div className="flex items-center justify-between gap-1">
        <span className="font-mono text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${pri.color}22`, color: pri.color }}>{pri.label}</span>
        <div className="flex items-center gap-1.5">
          {card.links?.length > 0 && <Link2 size={11} className="text-muted-foreground" />}
          {due && <span className={`font-mono text-[10px] ${overdue ? "text-destructive font-bold" : "text-muted-foreground"}`}>{due.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>}
        </div>
      </div>
      {card.stage === "Script" && (
        <div className="mt-1.5 pt-1.5 border-t border-border flex items-center gap-1 text-primary text-[10px] font-medium">
          <Sparkles size={11} /> AI draft
        </div>
      )}
    </div>
  );
}

function CalendarView({ cards, channels, month, setMonth, onCardClick }) {
  const year = month.getFullYear();
  const mon = month.getMonth();
  const monthLabel = month.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const firstDay = new Date(year, mon, 1);
  const startWeekday = firstDay.getDay(); // 0 Sun
  const daysInMonth = new Date(year, mon + 1, 0).getDate();

  // build grid cells (leading blanks + days)
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const cardsForDay = (d) => {
    if (!d) return [];
    return cards.filter((c) => {
      const dt = new Date(c.due_date);
      return dt.getFullYear() === year && dt.getMonth() === mon && dt.getDate() === d;
    });
  };

  const today = new Date();
  const isToday = (d) => d && today.getFullYear() === year && today.getMonth() === mon && today.getDate() === d;
  const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div data-testid="calendar-view">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-bold text-xl tracking-tight" data-testid="calendar-month-label">{monthLabel}</h3>
        <div className="flex items-center gap-2">
          <button data-testid="calendar-prev" onClick={() => setMonth(new Date(year, mon - 1, 1))}
            className="p-2 rounded-md border border-border text-muted-foreground hover:bg-secondary transition-colors duration-200"><ChevronLeft size={16} /></button>
          <button data-testid="calendar-today" onClick={() => { const n = new Date(); setMonth(new Date(n.getFullYear(), n.getMonth(), 1)); }}
            className="px-3 py-2 rounded-md border border-border text-sm text-muted-foreground hover:bg-secondary transition-colors duration-200">Today</button>
          <button data-testid="calendar-next" onClick={() => setMonth(new Date(year, mon + 1, 1))}
            className="p-2 rounded-md border border-border text-muted-foreground hover:bg-secondary transition-colors duration-200"><ChevronRight size={16} /></button>
        </div>
      </div>

      {/* Channel legend */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {channels.map((c) => (
          <span key={c.id} className="inline-flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: channelColor(c.id, channels) }} />
            {c.name}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-px bg-border border border-border rounded-lg overflow-hidden">
        {WEEKDAYS.map((w) => (
          <div key={w} className="bg-[#121214] px-2 py-2 text-center font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{w}</div>
        ))}
        {cells.map((d, i) => (
          <div key={i} className={`bg-[#0C0B0C] min-h-[92px] sm:min-h-[112px] p-1.5 ${d ? "" : "opacity-40"}`} data-testid={d ? `calendar-day-${d}` : undefined}>
            {d && (
              <div className={`text-[11px] font-mono mb-1 px-1 ${isToday(d) ? "text-primary font-bold" : "text-muted-foreground"}`}>
                {isToday(d) ? <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-primary text-primary-foreground">{d}</span> : d}
              </div>
            )}
            <div className="space-y-1">
              {cardsForDay(d).map((card) => (
                <button
                  key={card.id}
                  onClick={() => onCardClick(card)}
                  data-testid={`calendar-card-${card.id}`}
                  title={card.title}
                  className="block w-full text-left truncate rounded px-1.5 py-1 text-[11px] font-medium hover:brightness-125 transition-all duration-200"
                  style={{ background: `${channelColor(card.channel_id, channels)}26`, color: channelColor(card.channel_id, channels), borderLeft: `2px solid ${channelColor(card.channel_id, channels)}` }}
                >
                  {card.title}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CardModal({ card, channels, onClose, onSaved }) {
  const isNew = !card;
  const [form, setForm] = useState({
    title: card?.title || "",
    channel_id: card?.channel_id || channels[0]?.id || "",
    stage: card?.stage || "Idea",
    priority: card?.priority || "medium",
    due_date: card?.due_date ? card.due_date.slice(0, 10) : "",
    notes: card?.notes || "",
    links: (card?.links || []).join("\n"),
    script: card?.script || "",
  });
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const payload = () => ({
    title: form.title,
    channel_id: form.channel_id,
    stage: form.stage,
    priority: form.priority,
    due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
    notes: form.notes,
    links: form.links.split("\n").map((l) => l.trim()).filter(Boolean),
    script: form.script,
  });

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.channel_id) { toast.error("Pick a channel first"); return; }
    setSaving(true);
    try {
      if (isNew) await api.post("/cards", payload());
      else await api.put(`/cards/${card.id}`, payload());
      toast.success(isNew ? "Card created" : "Card updated");
      onSaved();
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!card) return;
    if (!window.confirm("Delete this card?")) return;
    await api.delete(`/cards/${card.id}`);
    toast.success("Card deleted");
    onSaved();
  };

  const draftAI = async () => {
    if (isNew) { toast.error("Save the card first, then draft with AI"); return; }
    setAiLoading(true);
    set("script", "");
    try {
      const token = localStorage.getItem("co_token");
      const res = await fetch(`${API}/cards/${card.id}/ai-draft`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok || !res.body) throw new Error("Stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      let streamErr = null;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") continue;
          try {
            const obj = JSON.parse(data);
            if (obj.delta) { acc += obj.delta; set("script", acc); }
            if (obj.error) streamErr = obj.error;
          } catch { /* ignore partial */ }
        }
      }
      if (streamErr) throw new Error(streamErr);
      if (!acc.trim()) throw new Error("Empty response");
      toast.success("Draft generated — edit before saving");
    } catch (e) {
      toast.error(e.message || "AI generation failed");
    } finally { setAiLoading(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div className="fixed inset-0 bg-black/70" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div
          onClick={(e) => e.stopPropagation()}
          data-testid="card-modal"
          className="relative w-full max-w-2xl max-h-[90vh] flex flex-col bg-[#121214] border border-border rounded-lg fade-up"
        >
          <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-border">
            <h2 className="font-display font-bold text-xl tracking-tight">{isNew ? "New card" : "Edit card"}</h2>
            <button onClick={onClose} data-testid="close-modal" className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
          </div>

          <div className="flex-1 min-h-0 px-6 py-5 space-y-4 overflow-y-auto">
          <Field label="Title">
            <input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="card-title-input"
              className="w-full bg-[#0A0A0B] border border-border rounded-md px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors duration-200" placeholder="Video title" />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Channel">
              <Select value={form.channel_id} onChange={(v) => set("channel_id", v)} testid="card-channel-select">
                {channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Field label="Stage">
              <Select value={form.stage} onChange={(v) => set("stage", v)} testid="card-stage-select">
                {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
              </Select>
            </Field>
            <Field label="Priority">
              <Select value={form.priority} onChange={(v) => set("priority", v)} testid="card-priority-select">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
              </Select>
            </Field>
            <Field label="Due date">
              <input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="card-due-input"
                className="w-full bg-[#0A0A0B] border border-border rounded-md px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors duration-200" />
            </Field>
          </div>

          <Field label="Notes">
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="card-notes-input"
              className="w-full bg-[#0A0A0B] border border-border rounded-md px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors duration-200 resize-none" placeholder="Angle, hooks, references..." />
          </Field>

          <Field label="Links (one per line)">
            <textarea value={form.links} onChange={(e) => set("links", e.target.value)} rows={2} data-testid="card-links-input"
              className="w-full bg-[#0A0A0B] border border-border rounded-md px-3 py-2.5 text-sm font-mono outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors duration-200 resize-none" placeholder="https://..." />
          </Field>

          {(form.stage === "Script" || form.script) && (
            <Field label="Script">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Generate a first draft, then edit freely.</span>
                <button
                  onClick={draftAI}
                  disabled={aiLoading}
                  data-testid="ai-draft-button"
                  className="flex items-center gap-1.5 bg-primary/15 text-primary border border-primary/40 rounded-md px-3 py-1.5 text-xs font-bold hover:bg-primary/25 transition-colors duration-200 disabled:opacity-60"
                >
                  {aiLoading ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}
                  {aiLoading ? "Drafting..." : "Draft with AI"}
                </button>
              </div>
              <textarea value={form.script} onChange={(e) => set("script", e.target.value)} rows={8} data-testid="card-script-input"
                className="w-full bg-[#0A0A0B] border border-border rounded-md px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors duration-200 resize-y" placeholder="Script draft will appear here..." />
            </Field>
          )}
        </div>

        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-t border-border">
          <div>
            {!isNew && (
              <button onClick={del} data-testid="delete-card-button" className="flex items-center gap-1.5 text-destructive text-sm font-medium hover:bg-destructive/10 rounded-md px-3 py-2 transition-colors duration-200">
                <Trash2 size={16} /> Delete
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="text-sm font-medium text-muted-foreground hover:text-foreground px-3 py-2">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="save-card-button"
              className="flex items-center gap-2 bg-primary text-primary-foreground font-bold rounded-md px-5 py-2.5 text-sm hover:bg-[#C29035] transition-colors duration-200 disabled:opacity-60">
              {saving ? <Loader2 className="animate-spin" size={16} /> : null}{isNew ? "Create" : "Save"}
            </button>
          </div>
        </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1.5 tracking-wide">{label}</label>
      {children}
    </div>
  );
}

function Select({ value, onChange, children, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full bg-[#0A0A0B] border border-border rounded-md px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors duration-200">
      {children}
    </select>
  );
}
